import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/site/Logo";

const links = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#solutions", label: "Solutions" },
  { href: "#capabilities", label: "Capabilities" },
  { href: "#gallery", label: "Gallery" },
  { href: "#contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.7, 0, 0.3, 1], delay: 0.4 }}
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-[var(--nv-paper)]/85 backdrop-blur-xl border-b border-[var(--nv-line)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[92px] flex items-center justify-between">
          <a href="#home" data-testid="nav-logo" className="flex items-center">
            <Logo variant="navy" size="lg" testId="nav-logo-mark" />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="nv-link font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--nv-navy)]/80 hover:text-[var(--nv-navy)]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href="#contact" data-testid="nav-get-in-touch" className="hidden md:inline-flex btn-gold">
              Get in Touch
            </a>
            <button
              type="button"
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-[var(--nv-navy)] rounded-full"
            >
              <Menu className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-[var(--nv-navy)] text-[var(--nv-paper)]"
            data-testid="mobile-menu"
          >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
              <Logo variant="light" testId="mobile-menu-logo" />
              <button
                type="button"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center w-10 h-10 border border-[var(--nv-paper)]/40 rounded-full"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <motion.nav
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } } }}
              className="px-8 md:px-16 mt-16 flex flex-col gap-6"
            >
              {links.map((l) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  variants={{ hidden: { y: 40, opacity: 0 }, show: { y: 0, opacity: 1 } }}
                  transition={{ duration: 0.6, ease: [0.7, 0, 0.3, 1] }}
                  className="font-serif text-4xl md:text-6xl tracking-tight"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </motion.a>
              ))}
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
